import { useEffect, useRef } from 'react'

/**
 * Hook to preserve and restore window scroll position.
 * Stores scroll position in sessionStorage when navigating away.
 * Restores it when returning to the same tab.
 * 
 * @param key - Unique key for storing scroll position (e.g., 'admin-books-table')
 * @param enabled - Whether scroll restoration is enabled (default: true)
 */
export function useScrollRestoration(key: string, enabled: boolean = true) {
  const scrollKey = `scroll-position-${key}`
  const isRestoringRef = useRef(false)
  const hasRestoredRef = useRef(false)
  const scrollWatcherRef = useRef<NodeJS.Timeout | null>(null)
  const watchAttemptsRef = useRef(0)
  const restoreScheduledRef = useRef(false)

  // Save scroll position before component unmounts or when navigating away
  useEffect(() => {
    if (!enabled) return

    const saveScrollPosition = () => {
      // Don't save if we're in the middle of restoring
      if (isRestoringRef.current) return
      
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      sessionStorage.setItem(scrollKey, String(scrollTop))
    }

    // Save on scroll (throttled)
    let scrollTimeout: NodeJS.Timeout
    const handleScroll = () => {
      // Don't track scroll while restoring
      if (isRestoringRef.current) return
      
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(saveScrollPosition, 150)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    // Also save on visibility change (when user navigates away)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveScrollPosition()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Save before page unload
    const handleBeforeUnload = () => {
      saveScrollPosition()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      clearTimeout(scrollTimeout)
      
      // Clean up scroll watcher if still running
      if (scrollWatcherRef.current) {
        clearInterval(scrollWatcherRef.current)
        scrollWatcherRef.current = null
      }
      
      // Don't save on unmount if navigating - the click handler already saved it
      // Only save if we haven't already saved a non-zero value
      const currentScroll = window.scrollY || document.documentElement.scrollTop
      const savedScroll = sessionStorage.getItem(scrollKey)
      // Only save if we haven't already saved a non-zero value (navigation already saved it)
      if (savedScroll === null || savedScroll === '0' || currentScroll > 0) {
        saveScrollPosition()
      }
    }
  }, [scrollKey, enabled])

  // Restore scroll position when component mounts
  useEffect(() => {
    if (!enabled) return
    if (hasRestoredRef.current) return // Already restored
    if (restoreScheduledRef.current) return // Already scheduled

    const savedPosition = sessionStorage.getItem(scrollKey)
    if (savedPosition === null || isNaN(Number.parseInt(savedPosition, 10))) return
    
    const scrollTop = Number.parseInt(savedPosition, 10)
    if (scrollTop === 0) return // No need to restore 0
    
    // Mark as scheduled to prevent duplicates
    restoreScheduledRef.current = true
    
    const MAX_WATCH_ATTEMPTS = 15
    let restoreAttempted = false

    const restoreScroll = () => {
      // Prevent duplicate restore calls
      if (restoreAttempted && watchAttemptsRef.current > 0) {
        return
      }
      restoreAttempted = true
      
      isRestoringRef.current = true
      
      // Scroll to position
      window.scrollTo({ top: scrollTop, behavior: 'auto' })
      
      // Clear any existing watcher
      if (scrollWatcherRef.current) {
        clearInterval(scrollWatcherRef.current)
      }
      
      // Watch for scroll being reset and restore again (handles React Router scroll reset)
      watchAttemptsRef.current = 0
      scrollWatcherRef.current = setInterval(() => {
        watchAttemptsRef.current++
        const checkScroll = window.scrollY
        
        // If scroll was reset to 0, restore again
        if (checkScroll < 50 && scrollTop > 100 && watchAttemptsRef.current < MAX_WATCH_ATTEMPTS) {
          window.scrollTo({ top: scrollTop, behavior: 'auto' })
        } else if (Math.abs(checkScroll - scrollTop) < 50) {
          // Success - scroll is stable
          if (scrollWatcherRef.current) {
            clearInterval(scrollWatcherRef.current)
            scrollWatcherRef.current = null
          }
          isRestoringRef.current = false
          hasRestoredRef.current = true
        } else if (watchAttemptsRef.current >= MAX_WATCH_ATTEMPTS) {
          // Max attempts reached
          if (scrollWatcherRef.current) {
            clearInterval(scrollWatcherRef.current)
            scrollWatcherRef.current = null
          }
          isRestoringRef.current = false
          hasRestoredRef.current = true
        }
      }, 100) // Check every 100ms
      
      // Auto-stop watcher after 2 seconds
      setTimeout(() => {
        if (scrollWatcherRef.current) {
          clearInterval(scrollWatcherRef.current)
          scrollWatcherRef.current = null
          isRestoringRef.current = false
        }
      }, 2000)
    }

    // Wait for React Router to finish its scroll-to-top behavior
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const pageHeight = document.documentElement.scrollHeight
          if (pageHeight >= scrollTop) {
            restoreScroll()
          } else {
            // Page not tall enough yet, wait a bit more
            setTimeout(restoreScroll, 200)
          }
        }, 100) // Small delay to let React Router finish
      })
    })
  }, [scrollKey, enabled])

  // Reset flags when key changes (different tab)
  useEffect(() => {
    hasRestoredRef.current = false
    restoreScheduledRef.current = false
    watchAttemptsRef.current = 0
    // Clear watcher if still running
    if (scrollWatcherRef.current) {
      clearInterval(scrollWatcherRef.current)
      scrollWatcherRef.current = null
    }
  }, [key])
}

